import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RiUserLine, RiLockPasswordLine, RiSaveLine, RiMailLine, RiPhoneLine } from 'react-icons/ri';
import api from '../../../services/api';
import toast from 'react-hot-toast';

const ProfileSettings = () => {
    const { t, i18n } = useTranslation();
    const isRTL = ['ar', 'ku'].includes(i18n.language);
    const [loading, setLoading] = useState(false);
    const [profileData, setProfileData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        username: '',
        phone: '',
    });

    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const response = await api.get('/users/profile');
            if (response.data.success) {
                const u = response.data.user;
                setProfileData({
                    first_name: u.firstName || u.first_name || '',
                    last_name: u.lastName || u.last_name || '',
                    email: u.email || '',
                    username: u.username || '',
                    phone: u.phone || '',
                });
            }
        } catch (error) {
            console.error('Fetch profile error:', error);
            toast.error(t('failed_to_load_user_data'));
        } finally {
            setLoading(false);
        }
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const response = await api.put('/users/profile', {
                firstName: profileData.first_name,
                lastName: profileData.last_name,
                email: profileData.email,
                phone: profileData.phone,
            });
            if (response.data.success) {
                toast.success(t('update_profile_success'));
            }
        } catch (error) {
            console.error('Update profile error:', error);
            toast.error(error.response?.data?.message || t('error_updating_profile'));
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordUpdate = async (e) => {
        e.preventDefault();
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            return toast.error(t('passwords_do_not_match'));
        }

        try {
            setLoading(true);
            const response = await api.put('/users/profile/password', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword,
            });
            if (response.data.success) {
                toast.success(t('update_password_success'));
                setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                });
            }
        } catch (error) {
            console.error('Update password error:', error);
            toast.error(error.response?.data?.message || t('error_updating_password'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Personal Details */}
            <section className="bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-6">
                    <div className="p-2 rounded-lg bg-red-50 text-red-600">
                        <RiUserLine size={20} />
                    </div>
                    <h2 className="text-lg font-bold text-zinc-900">{t('personal_details')}</h2>
                </div>

                <form onSubmit={handleProfileUpdate} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-semibold text-zinc-700 mb-2">
                            {t('first_name')}
                        </label>
                        <input
                            type="text"
                            value={profileData.first_name}
                            onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                            className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-zinc-700 mb-2">
                            {t('last_name')}
                        </label>
                        <input
                            type="text"
                            value={profileData.last_name}
                            onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                            className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-zinc-700 mb-2">
                            {t('email')}
                        </label>
                        <div className="relative">
                            <RiMailLine className={`absolute ${isRTL ? 'right-3.5' : 'left-3.5'} top-1/2 -translate-y-1/2 text-zinc-400 w-5 h-5`} />
                            <input
                                type="email"
                                value={profileData.email}
                                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                                className={`w-full ${isRTL ? 'pr-11 pl-4' : 'pl-11 pr-4'} py-2.5 rounded-xl border border-zinc-200 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all`}
                                required
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-zinc-700 mb-2">
                            {t('phone_number')}
                        </label>
                        <div className="relative">
                            <RiPhoneLine className={`absolute ${isRTL ? 'right-3.5' : 'left-3.5'} top-1/2 -translate-y-1/2 text-zinc-400 w-5 h-5`} />
                            <input
                                type="text"
                                value={profileData.phone}
                                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                                className={`w-full ${isRTL ? 'pr-11 pl-4' : 'pl-11 pr-4'} py-2.5 rounded-xl border border-zinc-200 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all`}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-zinc-700 mb-2">
                            {t('username')}
                        </label>
                        <input
                            type="text"
                            value={profileData.username}
                            disabled
                            className="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-200 text-zinc-500 rounded-xl cursor-not-allowed text-sm"
                        />
                    </div>

                    <div className="md:col-span-2 pt-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center gap-2 px-6 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all active:scale-95 disabled:opacity-50 font-bold shadow-lg shadow-red-100"
                        >
                            <RiSaveLine className="w-5 h-5" />
                            {loading ? t('saving') : t('save_changes')}
                        </button>
                    </div>
                </form>
            </section>

            {/* Security Section */}
            <section className="bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-6">
                    <div className="p-2 rounded-lg bg-red-50 text-red-600">
                        <RiLockPasswordLine size={20} />
                    </div>
                    <h2 className="text-lg font-bold text-zinc-900">{t('security_settings')}</h2>
                </div>

                <form onSubmit={handlePasswordUpdate} className="max-w-md space-y-5">
                    <div>
                        <label className="block text-sm font-semibold text-zinc-700 mb-2">
                            {t('current_password')}
                        </label>
                        <input
                            type="password"
                            value={passwordData.currentPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                            className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-zinc-700 mb-2">
                            {t('new_password')}
                        </label>
                        <input
                            type="password"
                            value={passwordData.newPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                            className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-zinc-700 mb-2">
                            {t('confirm_new_password')}
                        </label>
                        <input
                            type="password"
                            value={passwordData.confirmPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                            className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
                            required
                        />
                    </div>

                    <div className="pt-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2.5 bg-zinc-900 text-white rounded-xl hover:bg-zinc-800 transition-all active:scale-95 disabled:opacity-50 font-bold shadow-lg shadow-zinc-200"
                        >
                            {loading ? t('updating') : t('change_password')}
                        </button>
                    </div>
                </form>
            </section>
        </div>
    );
};

export default ProfileSettings;
